import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { BaseService } from 'src/app/core/services/base.httpService';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { InvoicedDatePopupComponent } from 'src/app/shared/components/invoiced-date-popup/invoiced-date-popup.component';
import { MessagePopupComponent } from 'src/app/shared/components/message-popup/message-popup.component';
import {
  DefaultErrorHandlerConfig,
  handleErrorMessage,
} from 'src/app/shared/operators/error.operator';
import { isMasterShipmentAggregatedValidationError } from 'src/app/shared/utility/master-shipment-linking.util';
import { ModalService } from 'src/app/shared/services/modal.service';
import { SnakbarService } from 'src/app/shared/services/snakbar.service';
import {
  ActionIcons,
  CrudActions,
  errorMessagesForAutoRate,
  errorMessagesForMasterShipmentLinking,
  HttpSignatures,
  ModalPopupMessages,
  pageType,
  popupWidth,
  rolesOptionalFields,
  StatusCodes,
  TransitPointValidationMessages,
} from 'src/app/shared/utility/constants';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import {
  ApplyBulkRoutingPayload,
  CreateHawbRouteApiResponse,
  CreateHawbRoutePayload,
  FetchHawbRouteApiResponse,
  FetchShipmentRoutesPayload,
  ScanShipmentStatusResponse,
} from '../model/hawb.model';
import {
  AddTransitPointPayload,
  EditTransitPointPayload,
  DeleteTransitPointPayload,
} from '../components/hawb/hawb-information/routes/routes.model';

export interface AutoRateSelection {
  ids: number[];
  isSameAgentGroup?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HawbService {
  public isCheckForMode = new BehaviorSubject<any>([]);
  public commodityIdForPage = new BehaviorSubject<number>(0);
  public clickedIcon = new BehaviorSubject<any>([]);
  public commodityData = new BehaviorSubject<any>([]);
  public commoditySelectedData = new BehaviorSubject<any>([]);
  public checkForRolesData = new BehaviorSubject<any>([]);
  public checkForRolesValidData = new BehaviorSubject<any>(false);
  public checkRolesDirty = new BehaviorSubject<boolean>(false);
  public checkForShipmentStatusData = new BehaviorSubject<boolean>(false);
  public paginationData = new BehaviorSubject<any>({});
  public paginationData$ = this.paginationData.asObservable();
  public mockFlag = false;
  public selectedIdsForBulkEdit = new BehaviorSubject<any>([]);
  public commodityListApiResponse = new BehaviorSubject<any>([]);
  public isPreviousPageSummary = new BehaviorSubject<any>('');
  public deletedApiCommodities = new BehaviorSubject<any>([]);
  public isHawbPrevSummary = new BehaviorSubject<any>('');
  public checkForConsigneeAndShipmentData = new BehaviorSubject<any>([]);
  public checkForAPIHits = new BehaviorSubject<any>(false);
  public checkDeleteRolesData = new BehaviorSubject<any>([]);
  public isCreatedLinkedData = new BehaviorSubject<any>('');
  public isHawbSummaryApiData = new BehaviorSubject<any>(false);
  public isCommoditySaved = new BehaviorSubject<boolean>(false);
  public isHsCodeFieldFocussed = new BehaviorSubject<boolean>(false);
  public totalCommodityValue$ = new BehaviorSubject<number>(0);
  public triggerTaxIdValidation$ = new BehaviorSubject<{
    countryCode: string;
    totalValue: number;
  } | null>(null);

  private readonly errorHandler = handleErrorMessage();

  constructor(
    public baseService: BaseService,
    private readonly modalService: ModalService,
    private readonly snackbarService: SnakbarService,
    private readonly errorHandlerService: ErrorHandlerService
  ) { }

  public setData(data: any): void {
    this.paginationData.next(data);
  }

  public getSearchData(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.getHawbList,
      event,
      '',
      'json'
    );
  }

  public getHawbByHawbId(hawbId: number): Observable<any> {
    const apiUrl = `${URLConstants.getHawbById}/hawbIds/${hawbId}`;
    return this.baseService.initiateApiRequest('GET', apiUrl, '', 'json');
  }

  public deleteHawbs(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.deleteHawb,
      event,
      '',
      'json'
    );
  }

  public setCommodityIdForPage(data: number) {
    this.commodityIdForPage.next(data);
  }

  public setMode(data: any) {
    this.isCheckForMode.next(data);
  }

  public updateClickedIcon(icon: string) {
    this.clickedIcon.next(icon);
  }

  public updateCommodityData(data: any) {
    this.commodityData.next(data);
  }

  public commodityDetails(data: any) {
    this.commoditySelectedData.next(data);
  }

  public checkRoleData(data: any) {
    this.checkForRolesData.next(data);
  }

  public CheckValidData(data: any) {
    this.checkForRolesValidData.next(data);
  }

  public CheckRolesDirtyData(data: boolean) {
    this.checkRolesDirty.next(data);
  }

  public CheckShipmentStatusData(data: boolean) {
    this.checkForShipmentStatusData.next(data);
  }

  public checkAccountDetails(): Observable<any> {
    return this.baseService.initiateApiRequest(
      'GET',
      URLConstants.getAccountDetails,
      '',
      'json'
    );
  }

  public checkCustomerDetails(data: string): Observable<any> {
    return this.baseService.initiateApiRequest(
      'GET',
      URLConstants.getCustomerDetails + data,
      '',
      'json'
    );
  }

  public saveHawb(data: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('PUT', URLConstants.saveHawbInformation, data)
      .pipe(this.hawbSaveUpdateErrorHandler());
  }

  public updateHawb(data: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.updateHawbInformation,
        data,
        'json'
      )
      .pipe(this.hawbSaveUpdateErrorHandler());
  }

  public getHawbData(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'GET',
      URLConstants.getHawbDataById + data,
      '',
      'json'
    )
    .pipe(this.errorHandler());
  }

  // SPEC-SSS-001 FR-SSS-UI-005: status lookup for the Scan Shipments page.
  public scanShipmentStatus(
    shipmentExt: string
  ): Observable<ScanShipmentStatusResponse> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.scanShipments.status + shipmentExt,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public getDeliveryAgentData(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'GET',
      URLConstants.getDeliveryAgentById + data,
      '',
      'json'
    );
  }

  public getMasterHawbData(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.getMasterHawbData,
      data,
      'json'
    ).pipe(this.errorHandler());
  }

  public validateOverageReference(data: {
    referenceType: 'mawb';
    referenceValue: string;
  }): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.overageValidateReference,
      data,
      'json'
    );
  }

  public blockUnblockHawbData(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.blockUnblockHawb,
      data,
      'json'
    );
  }

  public AddHawbNotesData(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.addHawbNotes,
      data,
      'json'
    );
  }

  public createErrorPopupData(
    message: string,
    title: string,
    isFooterRequired: boolean
  ) {
    this.modalService.openPopup(
      {
        message: message,
        isFooterRequired: isFooterRequired,
      },
      title,
      MessagePopupComponent,
      '360px'
    );
  }

  public setBulkEditIds(data: any) {
    this.selectedIdsForBulkEdit.next(data);
  }

  public setCommodityListDataFromResponse(data: any) {
    this.commodityListApiResponse.next(data);
  }

  public isSummaryPage(data: string) {
    this.isPreviousPageSummary.next(data);
  }

  public updateTotalCommodityValue(value: number) {
    this.totalCommodityValue$.next(value);
  }

  public setDeletedApiCommodities(data: any) {
    this.deletedApiCommodities.next(data);
  }

  public deleteCommodity(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.getDeleteCommodityDataById,
      event,
      '',
      'json'
    );
  }

  public saveCommodity(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.getSaveCommodityDataById,
      event,
      '',
      'json'
    );
  }

  public updateCommodity(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.getUpdateCommodityDataById,
      event,
      '',
      'json'
    );
  }

  public addRoles(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.addRoles,
      event,
      '',
      'json'
    );
  }

  public isHawbSummaryPrevSearch(data: string) {
    this.isHawbPrevSummary.next(data);
  }

  public checkConsigneeAndShipmentData(data: any) {
    this.checkForConsigneeAndShipmentData.next(data);
  }

  public apicallAfterChildEvents(data: any) {
    this.checkForAPIHits.next(data);
  }

  public ishawbSummaryApiData(data: boolean) {
    this.isHawbSummaryApiData.next(data);
  }

  public setDeletedRolesData(data: any) {
    this.checkDeleteRolesData.next(data);
  }

  public getHawbBulkEditUniqueData(hawbIds: string): Observable<any> {
    const url = URLConstants.uniqueHawbDataBulkEdit + hawbIds;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public updateBulkHawb(data: any): Observable<any> {
    const url = URLConstants.updateBulkHawb;
    return this.baseService.initiateApiRequest('POST', url, data, 'json');
  }

  public advanceHawb(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.advanceHawbInformation,
      data,
      'json'
    );
  }

  public bulkBaggingConditionsCheck(selectedData: any) {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.bulkBaggingPieces,
      selectedData,
      'json'
    );
  }

  public bulkGatewayingConditionsCheck(selectedData: {
    ids: number[];
    allowHeldGateway?: boolean;
    allowUnclearedGateway?: boolean;
  }) {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.bulkGatewayValidate,
      selectedData,
      'json'
    ).pipe(this.errorHandler());
  }

  public bulkGatewayShipments(bulkBagData: {
    ids: number[];
    allowHeldGateway?: boolean;
    allowUnclearedGateway?: boolean;
  }) {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.BulkGatewayShipments,
      bulkBagData,
      'json'
    );
  }

  public validateBulkRouting(selectedData: { ids: number[] }) {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.bulkRouteValidate,
        selectedData,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public applyBulkRouting(payload: ApplyBulkRoutingPayload) {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.bulkRouteApply,
        payload,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public getModifiedDataForCommodityRoles(commodityRoleData: any) {
    if (!Array.isArray(commodityRoleData)) {
      return;
    }
    for (let i = commodityRoleData.length - 1; i >= 0; i--) {
      const role = commodityRoleData[i];
      if (
        role?.isFromScreen === pageType.commodity &&
        role.action === CrudActions.update &&
        role.source !== CrudActions.ui
      ) {
        commodityRoleData.splice(i, 1);
      }
    }
    const propertiesToCheck = rolesOptionalFields;
    propertiesToCheck.forEach(property => {
      commodityRoleData.forEach((role: any) => {
        role.id = role.action === ActionIcons.add ? 1 : role.id;
        if (!role[property]) {
          delete role['isFromScreen'];
          delete role[property];
          delete role['commodityId'];
        }
        if (!role['zipCode']) {
          delete role.zipCode;
        }
      });
    });
  }

  public getShipmentGatewayValidation(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.validateGateway,
      event,
      'json'
    );
  }

  public gatewayedShipment(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.gatewayShipment,
      event,
      '',
      'json'
    );
  }

  public submitMultiPieceGateway(payload: {
    masterShipmentId: number;
    physicalPieceCount: number;
    selectedHawbIds?: number[];
    comments?: string;
    allowHeldGateway?: boolean;
    allowUnclearedGateway?: boolean;
  }): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.multiPieceGateway,
        payload,
        'json'
      )
      .pipe(this.errorHandler());
  }

  public setLinkedCriteriaData(data: any) {
    this.isCreatedLinkedData.next(data);
  }

  public setCommoditySavedStatus(data: boolean) {
    this.isCommoditySaved.next(data);
  }

  public getServiceBasedOnAccount(AccountIds: number): Observable<any> {
    const url = URLConstants.accountServiceType + AccountIds;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public setFocusToHsCodeFieldInCommodity(data: boolean) {
    this.isHsCodeFieldFocussed.next(data);
  }

  public validateThenAutoRate(event: AutoRateSelection): Observable<any> {
    return this.shipmentAutoRateValidationAPI(event).pipe(
      switchMap((validation: any) =>
        validation.statusCode === StatusCodes.Success
          ? this.autoRateWithInvoicedDate(event)
          : of(validation)
      )
    );
  }

  public autoRateWithInvoicedDate(event: AutoRateSelection): Observable<any> {
    return this.promptAutoRateInvoicedDate().pipe(
      switchMap((invDate: string) =>
        this.shipmentAutoRatingAPI({ ...event, invoiceDate: invDate })
      )
    );
  }

  private promptAutoRateInvoicedDate(): Observable<string> {
    return this.modalService
      .openPopup(
        {},
        ModalPopupMessages.autoRateInvoiceDateTitle,
        InvoicedDatePopupComponent,
        popupWidth.deletePopup
      )
      .afterClosed()
      .pipe(filter((invDate: string | undefined): invDate is string => !!invDate));
  }

  private shipmentAutoRateValidationAPI(
    event: AutoRateSelection
  ): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.autoRatingValidate,
      event,
      '',
      'json'
    );
  }

  private shipmentAutoRatingAPI(
    event: AutoRateSelection & { invoiceDate: string }
  ): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.autoRating,
      event,
      '',
      'json'
    );
  }

  public shipmentUnAutoRatingAPI(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.unAutoRating,
      event,
      '',
      'json'
    );
  }

  public createHawbRoute(
    payload: CreateHawbRoutePayload
  ): Observable<CreateHawbRouteApiResponse> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.createHawbRoute,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public fetchHawbRoute(id: number): Observable<FetchHawbRouteApiResponse> {
    return this.baseService
      .initiateApiRequest('GET', URLConstants.fetchHawbRoute + id, '', 'json')
      .pipe(this.errorHandler());
  }

  public getShipmentRoutes(
    payload: FetchShipmentRoutesPayload
  ): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.routing.getShipmentRoutes,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public addTransitPoint(payload: AddTransitPointPayload): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.addTransitPoint,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public editTransitPoint(payload: EditTransitPointPayload): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.editTransitPoint,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public handleEditTransitPoint(payload: EditTransitPointPayload): Observable<any> {
    return this.editTransitPoint(payload).pipe(
      tap((response: any) => {
        if (response?.statusCode === StatusCodes.Success) {
          this.snackbarService.openCustomisedSnackBar(
            TransitPointValidationMessages.editSuccess,
            ModalPopupMessages.success
          );
        }
      })
    );
  }

  public deleteTransitPoint(payload: DeleteTransitPointPayload): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteTransitPoint,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public validateServiceTypeForAccount(accountId: number, serviceType: string): Observable<any> {
    const apiUrl = `${URLConstants.validateServiceType}${accountId}/validateServiceType/${serviceType}`;
    return this.baseService.initiateApiRequest('GET', apiUrl, '', 'json');
  }

  public getAccountConfiguredServiceTypes(accountId: number): Observable<any> {
    const apiUrl = `${URLConstants.accountConfiguredServiceTypes}${accountId}/serviceTypes`;
    return this.baseService.initiateApiRequest('GET', apiUrl, '', 'json');
  }

  private hawbSaveUpdateErrorHandler() {
    const config = DefaultErrorHandlerConfig;
    return <T>(source: Observable<T>) =>
      source.pipe(
        tap((response: any) => {
          const code = response.statusCode ?? response.status;
          if (typeof response === 'string') {
            return;
          }
          if (
            ![StatusCodes.Success, StatusCodes.SuccessfulRequest].includes(code)
          ) {
            this.errorHandlerService.handleApiError(response);
          }
        }),
        catchError((response: HttpErrorResponse) => {
          if (isMasterShipmentAggregatedValidationError(response.error)) {
            const errorLines = response.error.details.messages.filter(
              (message: string) =>
                typeof message === 'string' && message.trim().length > 0
            );
            if (errorLines.length > 0) {
              this.openMasterShipmentValidationFailedPopup(errorLines);
            }
            return throwError(() => response);
          }
          if (config.showWarningErrorToast(response)) {
            this.errorHandlerService.handleApiError(
              {
                statusCode: StatusCodes.BadRequest,
                message: config.extractError(response),
              },
              ModalPopupMessages.Warning
            );
            return throwError(() => response);
          }
          if (config.showRedErrorToast(response)) {
            this.errorHandlerService.handleApiError({
              statusCode: StatusCodes.BadRequest,
              message: config.extractError(response),
            });
          } else if (response.error?.code && response.error?.message) {
            this.errorHandlerService.handleApiError({
              statusCode: response.status,
              message: response.error.message,
            });
          } else {
            this.errorHandlerService.handleError(response);
          }
          return throwError(() => response);
        })
      );
  }

  private openMasterShipmentValidationFailedPopup(messages: string[]): void {
    this.modalService.openPopup(
      {
        isFooterRequired: false,
        isForAutoRatingErrorLog: true,
        isForWarning: false,
        errorLogData: messages,
      },
      errorMessagesForMasterShipmentLinking.validationFailedLogHeader,
      MessagePopupComponent,
      popupWidth.searchPopup
    );
  }

  public handleAutoRatingSuccessfulRequestPopup(data: any): boolean {
    if (data?.data?.autoRatingFailed?.length > 0) {
      const failureDetailHeader = (data?.data?.failureDetailHeader ?? '')
        .toString()
        .trim();

      this.modalService.openPopup(
        {
          isFooterRequired: false,
          errorLogData: data?.data?.autoRatingFailed,
          isForAutoRatingErrorLog: true,
          isForWarning: false,
          autoRatingMessage: data?.message || '',
          ...(failureDetailHeader
            ? { autoRatingFailureDetailHeader: failureDetailHeader }
            : {}),
        },
        errorMessagesForAutoRate.autoRatingFailedLogHeader,
        MessagePopupComponent,
        popupWidth.searchPopup
      );
      return true;
    } else {
      this.modalService.openPopup(
        {
          isFooterRequired: false,
          message: data?.message,
          showCloseIcon: true,
        },
        ModalPopupMessages.Warning,
        MessagePopupComponent,
        popupWidth.deletePopup
      );
      return false;
    }
  }

  public getUFOShipments(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.getUFOShipments,
      event,
      '',
      'json'
    );
  }

  public updateUFOShipments(event: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.updateUFOShipments,
      event,
      '',
      'json'
    );
  }
}
